import { FieldType, MeasurementUnits, getEntityName, TableType } from '../../../../shared/enums';
import { FieldSettingsModel } from '../../common-models/field-settings.model';
import {
    RangeFieldValidator,
    RequiredFieldValidator,
    CustomFieldValidator
} from '../../../../shared/zoneiqui/field-validators';
import generalFieldDictionary from '../dictionary/calibration-general.dictionary';
import { ITableFieldsConfiguration } from 'src/app/interfaces/tables/table-fields-configuration.interface';

enum UseCapacitySections {
    RESIDENTIAL,
    LOGGING,
    OFFICE,
    COMMERCIAL,
    CIVIC,
    CIVIC_SUPPORT,
    EDUCATIONAL,
    INDUSTRIAL
}

const sections = [
    'Residential',
    'Lodging',
    'Office',
    'Commercial',
    'Civic',
    'Civic Support',
    'Educational',
    'Industrial'
];

function calibrationOfficeCommercialFieldsFactory() {
    return [
        new FieldSettingsModel('DensityNet', FieldType.NUMBER, MeasurementUnits.DU_PER_ACRE, {
            validators: [
                new RangeFieldValidator(0),
                new RequiredFieldValidator(model => model.IsLodgingExchangeEnabled && !model.DensityUL)
            ],
            disabledFunction: model => model.DensityUL,
            section: UseCapacitySections.RESIDENTIAL
        }),
        new FieldSettingsModel('Minimum1DwellingUnit', FieldType.BOOLEAN, MeasurementUnits.NONE, {
            disabledFunction: model => model.DensityUL, // || model.LodgingDensityUL,
            section: UseCapacitySections.RESIDENTIAL
        }),
        new FieldSettingsModel('DensityUL', FieldType.BOOLEAN, MeasurementUnits.NONE, {
            jointValidationFieldsList: ['Minimum1DwellingUnit', 'UnitAreaMin', 'DensityNet'],
            section: UseCapacitySections.RESIDENTIAL
        }),
        new FieldSettingsModel('UnitAreaMin', FieldType.NUMBER, MeasurementUnits.SQUARE_FEET, {
            validators: [new RangeFieldValidator(0), new RequiredFieldValidator(model => model.DensityUL)],
            section: UseCapacitySections.RESIDENTIAL
        }),
        new FieldSettingsModel('UnitAreaMax', FieldType.NUMBER, MeasurementUnits.SQUARE_FEET, {
            validators: [new RangeFieldValidator(0)],
            section: UseCapacitySections.RESIDENTIAL
        }),
        new FieldSettingsModel('MaximumResidentialPercentArea', FieldType.NUMBER, MeasurementUnits.PERCENT, {
            validators: [new RangeFieldValidator(0, 100)],
            section: UseCapacitySections.RESIDENTIAL
        }),
        new FieldSettingsModel('MaximumResidentialFAR', FieldType.NUMBER, MeasurementUnits.NONE, {
            validators: [new RangeFieldValidator(0)],
            section: UseCapacitySections.RESIDENTIAL
        }),
        new FieldSettingsModel('MaximumResidentialFLR', FieldType.NUMBER, MeasurementUnits.NONE, {
            validators: [new RangeFieldValidator(0)],
            section: UseCapacitySections.RESIDENTIAL
        }),

        new FieldSettingsModel('LodgingDensity', FieldType.NUMBER, MeasurementUnits.UNITS, {
            validators: [new RangeFieldValidator(0)],
            disabledFunction: model => model.LodgingDensityUL,
            section: UseCapacitySections.LOGGING
        }),
        new FieldSettingsModel('LodgingDensityUL', FieldType.BOOLEAN, MeasurementUnits.NONE, {
            jointValidationFieldsList: ['Minimum1DwellingUnit', 'LodgingDensity', 'RoomAreaMin'],
            section: UseCapacitySections.LOGGING
        }),
        new FieldSettingsModel('IsLodgingExchangeEnabled', FieldType.BOOLEAN, MeasurementUnits.NONE, {
            jointValidationFieldsList: ['LodgingExchange', 'DensityNet'],
            section: UseCapacitySections.LOGGING
        }),
        new FieldSettingsModel('LodgingExchange', FieldType.NUMBER, MeasurementUnits.NONE, {
            validators: [
                new RangeFieldValidator(0),
                new RequiredFieldValidator(model => model.IsLodgingExchangeEnabled)
            ],
            section: UseCapacitySections.LOGGING
        }),
        new FieldSettingsModel('RoomAreaMin', FieldType.NUMBER, MeasurementUnits.SQUARE_FEET, {
            validators: [new RangeFieldValidator(0), new RequiredFieldValidator(model => model.LodgingDensityUL)],
            section: UseCapacitySections.LOGGING
        }),
        new FieldSettingsModel('RoomAreaMax', FieldType.NUMBER, MeasurementUnits.SQUARE_FEET, {
            validators: [new RangeFieldValidator(0)],
            section: UseCapacitySections.LOGGING
        }),
        new FieldSettingsModel('MaximumLodgingPercentArea', FieldType.NUMBER, MeasurementUnits.PERCENT, {
            validators: [new RangeFieldValidator(0, 100)],
            section: UseCapacitySections.LOGGING
        }),
        new FieldSettingsModel('MaximumLodgingFAR', FieldType.NUMBER, MeasurementUnits.NONE, {
            validators: [new RangeFieldValidator(0)],
            section: UseCapacitySections.LOGGING
        }),
        new FieldSettingsModel('MaximumLodgingFLR', FieldType.NUMBER, MeasurementUnits.NONE, {
            validators: [new RangeFieldValidator(0)],
            section: UseCapacitySections.LOGGING
        }),

        new FieldSettingsModel('MaximumOfficePercentArea', FieldType.NUMBER, MeasurementUnits.PERCENT, {
            validators: [new RangeFieldValidator(0, 100)],
            section: UseCapacitySections.OFFICE
        }),
        new FieldSettingsModel('MaximumOfficeFAR', FieldType.NUMBER, MeasurementUnits.NONE, {
            validators: [new RangeFieldValidator(0)],
            section: UseCapacitySections.OFFICE
        }),
        new FieldSettingsModel('MaximumOfficeFLR', FieldType.NUMBER, MeasurementUnits.NONE, {
            validators: [new RangeFieldValidator(0)],
            section: UseCapacitySections.OFFICE
        }),
        new FieldSettingsModel('OfficeMaxStories', FieldType.NUMBER, MeasurementUnits.LEVELS, {
            validators: [
                new RangeFieldValidator(-1),
                new CustomFieldValidator(
                    `Value can't be more than ${generalFieldDictionary.PrincipalMaxHeightLvl.label}`,
                    ({ currentModelState, calibrationSet }) => {
                        const general = calibrationSet[getEntityName(TableType.CALIBRATION_GENERAL)].currentState;
                        return compareWithTheHighestElement(
                            currentModelState,
                            general,
                            'PrincipalMaxHeightLvl',
                            'OfficeMaxStories'
                        );
                    }
                ),
                new CustomFieldValidator(
                    `Value can't be more than ${generalFieldDictionary.TowerMaxHeightLvl.label}`,
                    ({ currentModelState, calibrationSet }) => {
                        const general = calibrationSet[getEntityName(TableType.CALIBRATION_GENERAL)].currentState;
                        return compareWithTheHighestElement(
                            currentModelState,
                            general,
                            'TowerMaxHeightLvl',
                            'OfficeMaxStories'
                        );
                    }
                ),
                new CustomFieldValidator(
                    `Value can't be more than ${generalFieldDictionary.PenthouseMaxHeightLvl.label}`,
                    ({ currentModelState, calibrationSet }) => {
                        const general = calibrationSet[getEntityName(TableType.CALIBRATION_GENERAL)].currentState;
                        return compareWithTheHighestElement(
                            currentModelState,
                            general,
                            'PenthouseMaxHeightLvl',
                            'OfficeMaxStories'
                        );
                    }
                )
            ],
            section: UseCapacitySections.OFFICE
        }),
        new FieldSettingsModel('OfficeCommercialMaxStories', FieldType.NUMBER, MeasurementUnits.LEVELS, {
            validators: [
                new RangeFieldValidator(-1),
                new CustomFieldValidator(
                    `Value can't be more than ${generalFieldDictionary.PrincipalMaxHeightLvl.label}`,
                    ({ currentModelState, calibrationSet }) => {
                        const general = calibrationSet[getEntityName(TableType.CALIBRATION_GENERAL)].currentState;
                        return compareWithTheHighestElement(
                            currentModelState,
                            general,
                            'PrincipalMaxHeightLvl',
                            'OfficeCommercialMaxStories'
                        );
                    }
                ),
                new CustomFieldValidator(
                    `Value can't be more than ${generalFieldDictionary.TowerMaxHeightLvl.label}`,
                    ({ currentModelState, calibrationSet }) => {
                        const general = calibrationSet[getEntityName(TableType.CALIBRATION_GENERAL)].currentState;
                        return compareWithTheHighestElement(
                            currentModelState,
                            general,
                            'TowerMaxHeightLvl',
                            'OfficeCommercialMaxStories'
                        );
                    }
                ),
                new CustomFieldValidator(
                    `Value can't be more than ${generalFieldDictionary.PenthouseMaxHeightLvl.label}`,
                    ({ currentModelState, calibrationSet }) => {
                        const general = calibrationSet[getEntityName(TableType.CALIBRATION_GENERAL)].currentState;
                        return compareWithTheHighestElement(
                            currentModelState,
                            general,
                            'PenthouseMaxHeightLvl',
                            'OfficeCommercialMaxStories'
                        );
                    }
                )
            ],
            section: UseCapacitySections.OFFICE
        }),
        new FieldSettingsModel('OfficeCommercialMaxPercentArea', FieldType.NUMBER, MeasurementUnits.PERCENT, {
            validators: [new RangeFieldValidator(0, 100)],
            section: UseCapacitySections.OFFICE
        }),

        new FieldSettingsModel('MaximumCommercialPercentArea', FieldType.NUMBER, MeasurementUnits.PERCENT, {
            validators: [new RangeFieldValidator(0, 100)],
            section: UseCapacitySections.COMMERCIAL
        }),
        new FieldSettingsModel('MaximumCommercialFAR', FieldType.NUMBER, MeasurementUnits.NONE, {
            validators: [new RangeFieldValidator(0)],
            section: UseCapacitySections.COMMERCIAL
        }),
        new FieldSettingsModel('MaximumCommercialFLR', FieldType.NUMBER, MeasurementUnits.NONE, {
            validators: [new RangeFieldValidator(0)],
            section: UseCapacitySections.COMMERCIAL
        }),
        new FieldSettingsModel('CommercialEstabMaxArea', FieldType.NUMBER, MeasurementUnits.SQUARE_FEET, {
            validators: [new RangeFieldValidator(0)],
            section: UseCapacitySections.COMMERCIAL
        }),
        new FieldSettingsModel('CommercialFoodMaxSeating', FieldType.NUMBER, MeasurementUnits.PEOPLE, {
            validators: [new RangeFieldValidator(0)],
            section: UseCapacitySections.COMMERCIAL
        }),

        new FieldSettingsModel('MaximumCivicPercentArea', FieldType.NUMBER, MeasurementUnits.PERCENT, {
            validators: [new RangeFieldValidator(0, 100)],
            section: UseCapacitySections.CIVIC
        }),
        new FieldSettingsModel('MaximumCivicFAR', FieldType.NUMBER, MeasurementUnits.NONE, {
            validators: [new RangeFieldValidator(0)],
            section: UseCapacitySections.CIVIC
        }),
        new FieldSettingsModel('MaximumCivicFLR', FieldType.NUMBER, MeasurementUnits.NONE, {
            validators: [new RangeFieldValidator(0)],
            section: UseCapacitySections.CIVIC
        }),

        new FieldSettingsModel('MaximumCivicSupportPercentArea', FieldType.NUMBER, MeasurementUnits.PERCENT, {
            validators: [new RangeFieldValidator(0, 100)],
            section: UseCapacitySections.CIVIC_SUPPORT
        }),
        new FieldSettingsModel('MaximumCivicSupportFAR', FieldType.NUMBER, MeasurementUnits.NONE, {
            validators: [new RangeFieldValidator(0)],
            section: UseCapacitySections.CIVIC_SUPPORT
        }),
        new FieldSettingsModel('MaximumCivicSupportFLR', FieldType.NUMBER, MeasurementUnits.NONE, {
            validators: [new RangeFieldValidator(0)],
            section: UseCapacitySections.CIVIC_SUPPORT
        }),

        new FieldSettingsModel('MaximumEducationalPercentArea', FieldType.NUMBER, MeasurementUnits.PERCENT, {
            validators: [new RangeFieldValidator(0, 100)],
            section: UseCapacitySections.EDUCATIONAL
        }),
        new FieldSettingsModel('MaximumEducationalFAR', FieldType.NUMBER, MeasurementUnits.NONE, {
            validators: [new RangeFieldValidator(0)],
            section: UseCapacitySections.EDUCATIONAL
        }),
        new FieldSettingsModel('MaximumEducationalFLR', FieldType.NUMBER, MeasurementUnits.NONE, {
            validators: [new RangeFieldValidator(0)],
            section: UseCapacitySections.EDUCATIONAL
        }),

        new FieldSettingsModel('MaximumIndustrialPercentArea', FieldType.NUMBER, MeasurementUnits.PERCENT, {
            validators: [new RangeFieldValidator(0, 100)],
            section: UseCapacitySections.INDUSTRIAL
        }),
        new FieldSettingsModel('MaximumIndustrialFAR', FieldType.NUMBER, MeasurementUnits.NONE, {
            validators: [new RangeFieldValidator(0)],
            section: UseCapacitySections.INDUSTRIAL
        }),
        new FieldSettingsModel('MaximumIndustrialFLR', FieldType.NUMBER, MeasurementUnits.NONE, {
            validators: [new RangeFieldValidator(0)],
            section: UseCapacitySections.INDUSTRIAL
        })
    ];
}

function getHighestBuildingElement(generalTable: any) {
    if (
        generalTable.PrincipalMaxHeightLvl === null &&
        generalTable.TowerMaxHeightLvl === null &&
        generalTable.PenthouseMaxHeightLvl === null
    ) {
        return null;
    }
    let label = generalFieldDictionary.PrincipalMaxHeightLvl;
    if (
        generalTable.TowerMaxHeightLvl &&
        ((!generalTable.PenthouseMaxHeightLvl &&
            generalTable.TowerMaxHeightLvl >= generalTable.PrincipalMaxHeightLvl) ||
            (generalTable.PenthouseMaxHeightLvl && generalTable.TowerMaxHeightLvl > generalTable.PenthouseMaxHeightLvl))
    ) {
        label = generalFieldDictionary.TowerMaxHeightLvl;
    } else if (
        generalTable.PenthouseMaxHeightLvl &&
        ((generalTable.TowerMaxHeightLvl && generalTable.TowerMaxHeightLvl <= generalTable.PenthouseMaxHeightLvl) ||
            (!generalTable.TowerMaxHeightLvl &&
                generalTable.PrincipalMaxHeightLvl <= generalTable.PenthouseMaxHeightLvl))
    ) {
        label = generalFieldDictionary.PenthouseMaxHeightLvl;
    }
    return label;
}

function compareWithTheHighestElement(model: any, general: any, generalField: string, useCapacityField: string) {
    if (!model || !general || !generalField || !useCapacityField) {
        return true;
    }
    const maxValue = Math.max(general.PrincipalMaxHeightLvl, general.TowerMaxHeightLvl, general.PenthouseMaxHeightLvl);
    const isTheHighestElement = getHighestBuildingElement(general) === generalFieldDictionary[generalField];
    return !(
        general.PenthouseMaxHeightLvl &&
        general.PenthouseMaxHeightLvl > 0 &&
        general.TowerMaxHeightLvl &&
        general.TowerMaxHeightLvl > 0 &&
        general.PrincipalMaxHeightLvl &&
        general.PrincipalMaxHeightLvl > 0 &&
        maxValue < model[useCapacityField] &&
        isTheHighestElement
    );
}

export default { fieldsFactory: calibrationOfficeCommercialFieldsFactory, sections } as ITableFieldsConfiguration;
